import { useEffect, useState } from "react";
import { ChevronLeft, HelpCircle, Monitor, QrCode, Scan, ShieldCheck, Smartphone } from "lucide-react";
import CameraPermissionComponent from "./CamMobileAcess";
import { browserName } from "react-device-detect";
import { useThemeStore } from "../store/useThemeStore";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import { useAuthStore } from "../store/useAuthStore";
import { isUnlimitedDeviceUsername } from "../constants/unlimitedDeviceUsernames";

interface ConnectedDevicesProps {
  onBack: () => void;
}

export interface DeviceInformations {
  browserName: string;
  deviceTypeName: string;
  deviceType?: "MOBILE" | "DESKTOP";
}

interface UserDevice {
  id: string;
  graphicAccountId: string;
  deviceId: string;
  browserName: string;
  deviceTypeName: string;
  deviceType: "MOBILE" | "DESKTOP";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getInformationsFromDevice = (): DeviceInformations => {
  const userAgent = navigator.userAgent;

  let deviceTypeName = "Desktop";

  if (/android/i.test(userAgent)) {
    deviceTypeName = "Android";
  } else if (/iPad|iPhone|iPod/.test(userAgent)) {
    deviceTypeName = "iOS";
  }

  const isMobile = /Mobi|Android/i.test(userAgent);
  if (!isMobile) {
    deviceTypeName = "Desktop";
  }

  return {
    browserName: browserName || "Desconhecido",
    deviceTypeName,
    deviceType: isMobile ? "MOBILE" : "DESKTOP",
  };
};

export default function ConnectedDevices({ onBack }: ConnectedDevicesProps) {
  const [sessions, setSessions] = useState<UserDevice[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const currentUserName = useAuthStore((s) => s.user?.userName);
  const unlimitedDevices = isUnlimitedDeviceUsername(currentUserName);
  const isLinkDisabled = unlimitedDevices ? false : sessions.length >= 2;
  const deviceCountLabel = unlimitedDevices
    ? `${sessions.length} dispositivo${sessions.length === 1 ? "" : "s"}`
    : `${sessions.length}/2 dispositivos`;
  const token = localStorage.getItem("@backoffice:token");
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformations>({
    browserName: "",
    deviceTypeName: "",
  });

  const { theme } = useThemeStore();
  const isDark = theme === "dark";
  const pageBg = isDark ? "bg-[#0f171c]" : "bg-[#F2F2F7]";
  const title = isDark ? "text-white" : "text-black";
  const muted = isDark ? "text-gray-400" : "text-[#8e8e93]";
  const subtitle = isDark ? "text-gray-400" : "text-[#6d6d72]";
  const cardBg = isDark ? "bg-[#162028]" : "bg-white";
  const cardBorder = isDark ? "border-white/10" : "border-[#e5e5ea]";
  const iconCircle = isDark ? "bg-[#22303a]" : "bg-[#eef3ff]";
  const badgeBg = isDark ? "bg-[#22303a]" : "bg-[#e5e5ea]";

  useEffect(() => {
    const { browserName: bn, deviceTypeName } = getInformationsFromDevice();
    setDeviceInfo({ browserName: bn, deviceTypeName });
    void getUserDevices();
  }, []);

  const getUserDevices = async () => {
    const { data } = await api.get("/userDevices/all", { headers: { Authorization: `Bearer ${token}` } });
    setSessions(data);
  };

  const handleUnlinkDevice = async (id: string, deviceId: string) => {
    const deviceIdLS = localStorage.getItem("@backoffice:deviceId");

    if (deviceIdLS === deviceId) {
      toast.error("Você não pode remover o dispositivo atual!");
      return;
    }

    await api.patch(`/userDevices/desactivate/${deviceId}`, { headers: { Authorization: `Bearer ${token}` } });
    await getUserDevices();
  };

  const formatDate = (input: string): string => {
    const date = new Date(input);
    if (isNaN(date.getTime())) return "Data inválida";

    const pad = (num: number) => String(num).padStart(2, "0");
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div
      className={`fixed inset-0 z-[1100] flex flex-col ${pageBg}`}
      style={{ fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      <ToastContainer />

      <header
        className={`flex-shrink-0 border-b backdrop-blur-xl ${
          isDark ? "bg-[rgba(17,27,33,0.92)] border-white/10" : "bg-white/90 border-[rgba(60,60,67,0.12)]"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <nav className="relative flex items-center justify-between px-2 h-11">
          <button
            type="button"
            className="flex items-center p-2 -ml-1 text-[#007AFF] active:opacity-60"
            onClick={onBack}
          >
            <ChevronLeft className="w-7 h-7" strokeWidth={2} />
          </button>
          <h1 className={`absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold ${title}`}>Dispositivos</h1>
          <button
            type="button"
            className="p-2 text-[#007AFF] active:opacity-60"
            onClick={() => setShowHelp((v) => !v)}
            aria-label="Ajuda"
          >
            <HelpCircle className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </nav>
      </header>

      {showHelp && (
        <div className={`mx-4 mt-3 rounded-xl border p-3 text-[13px] ${cardBg} ${cardBorder} ${subtitle}`}>
          <p className={`font-semibold mb-1 ${title}`}>Segurança Digital</p>
          <p>
            Verifique todos os dispositivos com acesso à sua conta escolar. Mantenha apenas os dispositivos que você
            reconhece.
          </p>
        </div>
      )}

      <main
        className="flex-1 overflow-y-auto px-4 pt-4 space-y-5"
        style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}
      >
        {/* Dispositivo atual */}
        <section
          className={`rounded-xl border px-5 py-6 flex flex-col items-center text-center shadow-sm ${cardBg} ${cardBorder}`}
        >
          <div className={`mb-3.5 flex h-16 w-16 items-center justify-center rounded-full ${iconCircle}`}>
            <Smartphone className="w-8 h-8 text-[#007AFF]" strokeWidth={1.6} />
          </div>
          <p className={`text-[17px] font-semibold ${title}`}>Seu Dispositivo Atual</p>
          <p className={`mt-1.5 text-[15px] ${muted}`}>
            {deviceInfo.deviceTypeName} • {deviceInfo.browserName}
          </p>
          <div className="mt-3.5 inline-flex items-center gap-1.5 rounded-full bg-[#34C759]/15 px-3.5 py-1.5">
            <span className="h-2 w-2 rounded-full bg-[#34C759]" />
            <span className="text-[13px] font-medium text-[#248a3d]">Online agora</span>
          </div>
        </section>

        {/* Vincular (UI reservada; lógica mantida) */}
        {false && (
          <section className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className={`text-[13px] uppercase tracking-wide ${muted}`}>Vincular Novo Dispositivo</h2>
              <span className={`text-[12px] font-medium px-2.5 py-1 rounded-full ${badgeBg} ${title}`}>
                {deviceCountLabel}
              </span>
            </div>
            <button
              type="button"
              disabled={isLinkDisabled}
              className={`w-full py-4 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 ${
                isLinkDisabled
                  ? "bg-transparent border-[#c7c7cc] text-[#8e8e93]"
                  : `border-[#007AFF]/40 ${cardBg} text-[#007AFF]`
              }`}
              onClick={() => !isLinkDisabled && setShowCamera(true)}
            >
              {isLinkDisabled ? (
                <>
                  <Scan className="w-5 h-5" />
                  <p className="text-[15px] font-medium">Limite de dispositivos atingido</p>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-[#007AFF]/12 flex items-center justify-center">
                    <CameraPermissionComponent />
                  </div>
                  <p className="text-[15px] font-medium">Escanear QR Code</p>
                </>
              )}
            </button>
          </section>
        )}

        {!unlimitedDevices && isLinkDisabled && (
          <div className="rounded-xl border border-[#FFD60A]/50 bg-[#FFD60A]/15 p-3 text-[13px] text-[#9a6700]">
            <p className="font-semibold">Limite atingido!</p>
            <p className="mt-0.5">Você já tem 2 dispositivos vinculados. Remova um para adicionar outro.</p>
          </div>
        )}

        {/* Vinculados */}
        <section>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className={`text-[13px] uppercase tracking-wide ${muted}`}>Dispositivos Vinculados</h2>
            <span className={`text-[12px] font-medium px-2.5 py-1 rounded-full ${badgeBg} ${title}`}>
              {deviceCountLabel}
            </span>
          </div>

          {sessions.length === 0 ? (
            <div className={`rounded-xl border px-4 py-8 text-center ${cardBg} ${cardBorder}`}>
              <p className={`text-[15px] ${muted}`}>Nenhum dispositivo vinculado</p>
            </div>
          ) : (
            <div className={`rounded-xl border overflow-hidden ${cardBg} ${cardBorder}`}>
              {sessions.map((device, idx) => (
                <div
                  key={device.id}
                  className={`flex items-center gap-3.5 px-4 min-h-[68px] py-3 ${
                    idx > 0 ? `border-t ${cardBorder}` : ""
                  }`}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconCircle}`}>
                    {device.deviceType === "MOBILE" ? (
                      <Smartphone className="w-5 h-5 text-[#007AFF]" strokeWidth={1.75} />
                    ) : (
                      <Monitor className="w-5 h-5 text-[#007AFF]" strokeWidth={1.75} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-[17px] font-semibold truncate ${title}`}>
                        {device.deviceType.charAt(0).toUpperCase() + device.deviceType.slice(1).toLowerCase()}
                      </p>
                      <button
                        type="button"
                        className="text-[16px] font-medium text-[#FF3B30] active:opacity-60 shrink-0 px-1 py-2 -my-2"
                        onClick={() => handleUnlinkDevice(device.id, device.deviceId)}
                      >
                        Remover
                      </button>
                    </div>
                    <p className={`text-[13px] mt-0.5 truncate ${muted}`}>
                      {device.browserName}
                      <span className="mx-1.5">•</span>
                      {device.updatedAt ? formatDate(device.updatedAt) : formatDate(device.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Dica de segurança */}
        <div className={`rounded-xl border p-4 flex gap-3 ${cardBg} ${cardBorder}`}>
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#34C759]/15">
            <ShieldCheck className="w-5 h-5 text-[#34C759]" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <p className={`text-[15px] font-semibold mb-1 ${title}`}>Dica de Segurança</p>
            <p className={`text-[13px] leading-snug ${subtitle}`}>
              Remova dispositivos que você não reconhece ou não usa mais para manter sua conta mais segura.
            </p>
          </div>
        </div>
      </main>

      {showCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className={`w-full max-w-md overflow-hidden rounded-2xl ${cardBg}`}>
            <div className="bg-[#007AFF] text-white p-4 flex justify-between items-center">
              <h3 className="font-semibold text-[17px]">Escanear QR Code</h3>
              <button type="button" className="p-1 active:opacity-70" onClick={() => setShowCamera(false)}>
                <span className="text-[22px] leading-none">×</span>
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <div className="relative mb-6 flex h-64 w-64 items-center justify-center overflow-hidden rounded-xl border-4 border-[#007AFF]">
                <div className="absolute inset-0 flex items-center justify-center bg-[#F2F2F7]">
                  <div className="text-center">
                    <QrCode className="w-16 h-16 text-[#8e8e93] mx-auto" />
                    <p className="text-[#8e8e93] mt-2 text-[13px]">Posicione o QR Code na área</p>
                  </div>
                </div>
              </div>
              <p className={`text-center mb-4 text-[14px] ${subtitle}`}>
                Use a câmera do dispositivo para escanear o QR Code exibido no novo dispositivo que deseja vincular.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  className={`px-4 py-2.5 rounded-xl text-[15px] font-medium ${badgeBg} ${title}`}
                  onClick={() => setShowCamera(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="px-4 py-2.5 rounded-xl bg-[#007AFF] text-white text-[15px] font-medium flex items-center gap-2"
                  onClick={() => {
                    setTimeout(() => {
                      setShowCamera(false);
                      alert("Dispositivo vinculado com sucesso!");
                    }, 1000);
                  }}
                >
                  <Scan className="w-4 h-4" />
                  Simular Leitura
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
